/**
 * RFC 8785 JSON Canonicalization Scheme (JCS).
 *
 * - Object members are sorted by their UTF-16 code units (JS default string sort).
 * - Numbers use the ECMAScript Number serialization (what JSON.stringify emits); NaN/±Infinity throw.
 * - Strings use JSON.stringify escaping, which matches JCS; lone surrogates throw (I-JSON).
 * - `undefined` object members are omitted (convenient for optional fields); `undefined` in an
 *   array, functions, symbols, bigints and non-plain objects throw.
 */
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;
export class CanonicalizationError extends Error {
    name = 'CanonicalizationError';
}
export function canonicalize(value) {
    return serialize(value, new Set());
}
function serializeString(s) {
    if (LONE_SURROGATE.test(s))
        throw new CanonicalizationError('lone surrogate in string');
    return JSON.stringify(s);
}
function serialize(value, stack) {
    if (value === null)
        return 'null';
    switch (typeof value) {
        case 'boolean':
            return value ? 'true' : 'false';
        case 'number':
            if (!Number.isFinite(value))
                throw new CanonicalizationError('non-finite number');
            // JSON.stringify implements ECMAScript Number serialization and maps -0 to "0".
            return JSON.stringify(value);
        case 'string':
            return serializeString(value);
        case 'object':
            break;
        default:
            throw new CanonicalizationError(`unsupported type: ${typeof value}`);
    }
    const obj = value;
    if (stack.has(obj))
        throw new CanonicalizationError('cyclic structure');
    stack.add(obj);
    try {
        if (Array.isArray(obj)) {
            const parts = [];
            for (const item of obj) {
                if (item === undefined)
                    throw new CanonicalizationError('undefined in array');
                parts.push(serialize(item, stack));
            }
            return `[${parts.join(',')}]`;
        }
        const proto = Object.getPrototypeOf(obj);
        if (proto !== Object.prototype && proto !== null) {
            throw new CanonicalizationError('only plain objects can be canonicalized');
        }
        const record = obj;
        const keys = Object.keys(record)
            .filter((k) => record[k] !== undefined)
            .sort();
        const parts = keys.map((k) => `${serializeString(k)}:${serialize(record[k], stack)}`);
        return `{${parts.join(',')}}`;
    }
    finally {
        stack.delete(obj);
    }
}
//# sourceMappingURL=jcs.js.map